import { useEffect, useState } from "react";
import { api, API } from "../../lib/api";
import { Plus, Pencil, Trash2, Copy, FileText, Download, RefreshCw, X, Eye, Undo2, CheckCircle2, ChevronDown, ChevronUp, GripVertical } from "lucide-react";

const TYPE_LABELS = {
  text: "Testo",
  number: "Numero",
  date: "Data",
  importo: "Importo (€)",
  checkbox: "Casella (sì/no)",
  radio: "Scelta multipla",
  select: "Menu a tendina",
  textarea: "Area di testo",
};

const STATUS_LABELS = {
  da_compilare: { label: "Da compilare", color: "bg-[#FEF3C7] text-[#B45309]" },
  bozza: { label: "Bozza salvata", color: "bg-[#DBEAFE] text-[#1E40AF]" },
  compilato: { label: "Compilato", color: "bg-[#EAF8F0] text-[#007A3D]" },
  approvato: { label: "Approvato", color: "bg-[#D1FAE5] text-[#065F46]" },
  da_correggere: { label: "Da correggere", color: "bg-[#FEE2E2] text-[#B91C1C]" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT"); } catch { return iso; }
}

function FieldRow({ f, idx, count, onChange, onRemove, onMove }) {
  const [open, setOpen] = useState(false);
  const needsOpts = f.type === "radio" || f.type === "select";
  return (
    <div className="border border-[#E5E7EB] rounded-2xl bg-white" data-testid={`form-field-${idx}`}>
      <div className="flex items-center gap-2 p-3">
        <GripVertical className="w-4 h-4 text-[#9CA3AF]" />
        <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-2">
          <input
            className="md:col-span-4 rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm"
            placeholder="Etichetta (es. Ragione Sociale)"
            value={f.label || ""}
            onChange={(e) => onChange({ ...f, label: e.target.value, key: f.key || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") })}
          />
          <input
            className="md:col-span-3 rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm font-mono"
            placeholder="chiave_univoca"
            value={f.key || ""}
            onChange={(e) => onChange({ ...f, key: e.target.value })}
          />
          <select
            className="md:col-span-3 rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm bg-white"
            value={f.type}
            onChange={(e) => onChange({ ...f, type: e.target.value })}
          >
            {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <label className="md:col-span-2 flex items-center gap-2 text-sm text-[#1A1A1A] px-2">
            <input type="checkbox" checked={!!f.required} onChange={(e) => onChange({ ...f, required: e.target.checked })} />
            Obbligatorio
          </label>
        </div>
        <button type="button" title="Espandi" onClick={() => setOpen((v) => !v)} className="p-1.5 rounded-full hover:bg-[#F9FAFB]">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        <button type="button" title="Sposta su" disabled={idx === 0} onClick={() => onMove(idx, idx - 1)} className="p-1.5 rounded-full hover:bg-[#F9FAFB] disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
        <button type="button" title="Sposta giù" disabled={idx === count - 1} onClick={() => onMove(idx, idx + 1)} className="p-1.5 rounded-full hover:bg-[#F9FAFB] disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
        <button type="button" title="Elimina" onClick={onRemove} className="p-1.5 rounded-full hover:bg-[#FEE2E2] text-[#B91C1C]"><Trash2 className="w-4 h-4" /></button>
      </div>
      {open && (
        <div className="border-t border-[#E5E7EB] p-3 space-y-2">
          <input
            className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm"
            placeholder="Placeholder (opzionale)"
            value={f.placeholder || ""}
            onChange={(e) => onChange({ ...f, placeholder: e.target.value })}
          />
          <input
            className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm"
            placeholder="Testo di aiuto (opzionale)"
            value={f.help || ""}
            onChange={(e) => onChange({ ...f, help: e.target.value })}
          />
          {needsOpts && (
            <textarea
              rows={3}
              className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm font-mono"
              placeholder="Opzioni (una per riga)"
              value={(f.options || []).join("\n")}
              onChange={(e) => onChange({ ...f, options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
            />
          )}
        </div>
      )}
    </div>
  );
}

function FormEditor({ clientId, initial, onSaved, onCancel }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [category, setCategory] = useState(initial?.category || "Altri Documenti");
  const [fields, setFields] = useState(initial?.fields || []);
  const [pdfFile, setPdfFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const isNew = !initial;

  const addField = () => setFields([...fields, { label: "", key: "", type: "text", required: false }]);
  const updateField = (i, v) => { const c = [...fields]; c[i] = v; setFields(c); };
  const removeField = (i) => setFields(fields.filter((_, idx) => idx !== i));
  const moveField = (i, j) => { const c = [...fields]; [c[i], c[j]] = [c[j], c[i]]; setFields(c); };

  const save = async () => {
    if (!title.trim()) { alert("Titolo obbligatorio"); return; }
    if (fields.some((f) => !f.label?.trim())) { alert("Ogni campo deve avere un'etichetta"); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem("bg_token");
      if (isNew) {
        const form = new FormData();
        form.append("payload", JSON.stringify({ title, description, category, fields }));
        if (pdfFile) form.append("pdf", pdfFile);
        const res = await fetch(`${API}/admin/clients/${clientId}/fillable-forms`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Errore creazione"); }
      } else {
        await api.put(`/admin/fillable-forms/${initial.id}`, { title, description, category, fields });
      }
      onSaved();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 space-y-4">
      <h4 className="font-display font-bold text-lg text-[#1A1A1A]">{isNew ? "Nuovo documento compilabile" : "Modifica documento compilabile"}</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-1">Titolo *</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm" placeholder="Modulo Anagrafica Cliente" />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-1">Categoria</label>
          <input value={category} onChange={(e) => setCategory(e.target.value)} className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm" />
        </div>
        <div className="md:col-span-3">
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-1">Descrizione</label>
          <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm" />
        </div>
      </div>
      {isNew && (
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-1">Modello PDF (opzionale)</label>
          <input type="file" accept="application/pdf" onChange={(e) => setPdfFile(e.target.files?.[0] || null)} className="text-sm" />
          <p className="text-xs text-[#6B7280] mt-1">Il PDF sarà consultabile dal cliente durante la compilazione e allegato al documento finale.</p>
        </div>
      )}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#007A3D]">Campi compilabili</div>
          <button onClick={addField} data-testid="add-field" className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full bg-[#EAF8F0] text-[#007A3D] hover:bg-[#00A651] hover:text-white transition-colors"><Plus className="w-3.5 h-3.5" /> Aggiungi campo</button>
        </div>
        <div className="space-y-2">
          {fields.length === 0 && <div className="text-sm text-[#6B7280] py-4 text-center bg-[#F9FAFB] rounded-xl">Nessun campo. Aggiungi il primo.</div>}
          {fields.map((f, i) => (
            <FieldRow key={i} f={f} idx={i} count={fields.length}
              onChange={(v) => updateField(i, v)} onRemove={() => removeField(i)} onMove={moveField} />
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm rounded-full border border-[#E5E7EB]">Annulla</button>
        <button data-testid="save-form" onClick={save} disabled={saving} className="px-5 py-2 text-sm rounded-full bg-[#007A3D] hover:bg-[#00A651] text-white disabled:opacity-60">
          {saving ? "Salvataggio…" : "Salva documento"}
        </button>
      </div>
    </div>
  );
}

export default function FillableFormsPanel({ client }) {
  const [forms, setForms] = useState([]);
  const [view, setView] = useState("list");
  const [editing, setEditing] = useState(null);

  const load = () => api.get(`/admin/clients/${client.id}/fillable-forms`).then((r) => setForms(r.data || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [client.id]);

  const downloadPdf = async (fid, endpoint, filename) => {
    const token = localStorage.getItem("bg_token");
    const res = await fetch(`${API}/admin/fillable-forms/${fid}/${endpoint}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { alert("Download fallito"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const del = async (f) => {
    if (!confirm(`Eliminare "${f.title}"?`)) return;
    await api.delete(`/admin/fillable-forms/${f.id}`); load();
  };
  const reopen = async (f) => { await api.post(`/admin/fillable-forms/${f.id}/reopen`); load(); };
  const approve = async (f) => { await api.post(`/admin/fillable-forms/${f.id}/approve`); load(); };
  const duplicate = async (f) => { await api.post(`/admin/fillable-forms/${f.id}/duplicate`); load(); };

  if (view === "new") return <FormEditor clientId={client.id} onSaved={() => { setView("list"); load(); }} onCancel={() => setView("list")} />;
  if (view === "edit" && editing) return <FormEditor clientId={client.id} initial={editing} onSaved={() => { setView("list"); setEditing(null); load(); }} onCancel={() => { setView("list"); setEditing(null); }} />;

  return (
    <div className="space-y-3" data-testid="fillable-forms-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h4 className="font-display font-bold text-base text-[#1A1A1A]">Documenti compilabili</h4>
          <p className="text-xs text-[#6B7280]">Assegna moduli online che il cliente può compilare direttamente.</p>
        </div>
        <button data-testid="new-form" onClick={() => setView("new")} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-full bg-[#007A3D] hover:bg-[#00A651] text-white"><Plus className="w-4 h-4" /> Nuovo compilabile</button>
      </div>
      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        {forms.length === 0 ? (
          <div className="p-8 text-center text-[#6B7280] text-sm">Nessun documento compilabile ancora assegnato.</div>
        ) : (
          <ul className="divide-y divide-[#F3F4F6]">
            {forms.map((f) => {
              const st = STATUS_LABELS[f.status] || STATUS_LABELS.da_compilare;
              return (
                <li key={f.id} data-testid={`form-row-${f.id}`} className="px-5 py-4 flex flex-col md:flex-row md:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#1A1A1A] text-sm">{f.title}</span>
                      <span className={`text-[10px] font-bold uppercase tracking-[0.1em] px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
                    </div>
                    <div className="text-xs text-[#6B7280] mt-0.5">
                      {f.category} · {f.fields?.length || 0} campi · agg. {fmtDate(f.updated_at)}
                      {f.submitted_at && <> · compilato il {fmtDate(f.submitted_at)}</>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {f.has_submitted_pdf && (
                      <button title="Scarica compilato" onClick={() => downloadPdf(f.id, "pdf-submitted", `${f.title}.pdf`)} className="p-2 rounded-full bg-[#EAF8F0] text-[#007A3D] hover:bg-[#00A651] hover:text-white"><Download className="w-4 h-4" /></button>
                    )}
                    {f.has_template_pdf && (
                      <button title="Modello PDF" onClick={() => downloadPdf(f.id, "pdf-template", `modello-${f.title}.pdf`)} className="p-2 rounded-full hover:bg-[#F9FAFB]"><FileText className="w-4 h-4" /></button>
                    )}
                    {(f.status === "compilato") && (
                      <button title="Approva" onClick={() => approve(f)} className="p-2 rounded-full hover:bg-[#EAF8F0] text-[#007A3D]"><CheckCircle2 className="w-4 h-4" /></button>
                    )}
                    {(f.status === "compilato" || f.status === "approvato") && (
                      <button title="Riapri per modifiche" onClick={() => reopen(f)} className="p-2 rounded-full hover:bg-[#FEF3C7] text-[#B45309]"><Undo2 className="w-4 h-4" /></button>
                    )}
                    <button title="Duplica" onClick={() => duplicate(f)} className="p-2 rounded-full hover:bg-[#F9FAFB]"><Copy className="w-4 h-4" /></button>
                    <button title="Modifica" onClick={() => { setEditing(f); setView("edit"); }} className="p-2 rounded-full hover:bg-[#F9FAFB]"><Pencil className="w-4 h-4" /></button>
                    <button title="Elimina" onClick={() => del(f)} className="p-2 rounded-full hover:bg-[#FEE2E2] text-[#B91C1C]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
